// API レスポンス型定義

// 達成タイプの列挙型
export enum AchievementType {
  CHAMPIONSHIP = "championship",  // 優勝
  FINALIST = "finalist",  // 優勝＋準優勝
  SEMIFINAL = "semifinal",  // ベスト4以上
  QUARTERFINAL = "quarterfinal",  // ベスト8以上
}

export interface TournamentOption {
  year: number;
  name: string;
}

export interface PrefectureOption {
  id: number;
  name: string;
}

export interface TeamOption {
  id: number;
  name: string;
}

export interface CategoryOption {
  id: string;
  name: string;
}

export interface GenerationOption {
  id: string;
  name: string;
}

export interface FilterOptionsResponse {
  tournaments: TournamentOption[];
  prefectures: PrefectureOption[];
  teams: TeamOption[];
  categories: CategoryOption[];
  generations: GenerationOption[];
}

export interface DecadeSummary {
  decade: string;
  totalMatches: number;
  totalGoals: number;
  totalPkMatches: number;
}

export interface Summary {
  totalMatches: number;
  totalGoals: number;
  totalGoalsAgainst: number;
  totalPkMatches: number;
  byDecade: DecadeSummary[];
}

export interface ChampionshipByPrefecture {
  prefecture: string;
  count: number;
}

export interface WinsByTeam {
  team: string;
  wins: number;
}

export interface GoalsPerTournament {
  year: number;
  goals: number;
}

export interface ChampionshipLocation {
  team_name: string;
  prefecture: string;
  latitude: number;
  longitude: number;
  championship_count: number;
  achievement_type: AchievementType;
}

export interface Charts {
  championshipsByPrefecture: ChampionshipByPrefecture[];
  winsByTeam: WinsByTeam[];
  goalsPerTournament: GoalsPerTournament[];
  championshipLocations: ChampionshipLocation[];
}

export interface MatchTableItem {
  tournament: string;
  round: string;
  winner: string;
  score: string;
  loser: string;
  notes: string;
}

export interface TableView {
  matches: MatchTableItem[];
  totalCount: number;
}

export interface DashboardResponse {
  summary: Summary;
  charts: Charts;
  tableView: TableView;
  topWinRatePrefectures: TopWinRatePrefectureItem[];
  topMatchCountPrefectures: TopMatchCountPrefectureItem[];
}

// フィルター状態の型定義
export interface FilterState {
  selectedCategory: string | null;
  startYear: number | null;
  endYear: number | null;
  selectedPrefectures: number[];
  selectedTeams: number[];
  achievementType: AchievementType;
}

// 都道府県別ダッシュボード用の型定義
export interface PrefectureSummary {
  totalTournaments: number;
  totalMatches: number;
  totalWins: number;
  totalLosses: number;
  totalDraws: number;
  winRate: number;
  totalGoals: number;
  totalGoalsAgainst: number;
  goalDifference: number;
  averageGoalsPerMatch: number;
  championships: number;
  runnersUp: number;
  semifinals: number;
  quarterfinals: number;
  totalTeams: number;
  averageTeamsPerTournament: number;
  totalInterPrefectureMatches: number;
  interPrefectureWinRate: number;
  strongestOpponent: string | null;
  weakestOpponent: string | null;
}

export interface PrefectureGenerationStats {
  generation: string;
  tournaments: number;
  matches: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  championships: number;
  runnersUp: number;
  semifinals: number;
  totalGoals: number;
  totalGoalsAgainst: number;
  goalDifference: number;
  averageGoalsPerMatch: number;
  interPrefectureMatches: number;
  interPrefectureWinRate: number;
}

export interface InterPrefectureMatchResult {
  opponentPrefectureId: number;
  opponentPrefectureName: string;
  totalMatches: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  totalGoals: number;
  totalGoalsAgainst: number;
  goalDifference: number;
  lastMatchResult: string | null;
  lastMatchDate: string | null;
  lastMatchTournament: string | null;
}

export interface InterPrefectureAnalysis {
  allOpponents: InterPrefectureMatchResult[];
  opponentsByWinRate: InterPrefectureMatchResult[];
  opponentsByMatchCount: InterPrefectureMatchResult[];
  vsStrongOpponents: InterPrefectureMatchResult[];
  vsWeakOpponents: InterPrefectureMatchResult[];
}

export interface PrefectureRankingItem {
  rank: number;
  prefectureId: number;
  prefectureName: string;
  totalChampionships: number;
  totalRunnersUp: number;
  totalSemifinals: number;
  totalQuarterfinals: number;
  winRate: number;
  totalMatches: number;
  totalWins: number;
  totalLosses: number;
  totalPkWins: number;
  totalPkLosses: number;
  totalGoals: number;
  averageGoalsPerMatch: number;
  goalDifference: number;
  totalTournaments: number;
  averageTeamsPerTournament: number;
  interPrefectureWinRate: number;
  vsTop10WinRate: number;
  vsBottom10WinRate: number;
}

export interface ChampionshipHistoryItem {
  year: number;
  tournamentName: string;
  generation: string;
  championTeam: string;
  runnerUpTeam: string;
  finalScore: string;
  prefectureName: string;
  isHomeChampionship: boolean;
  defeatedOpponents: string[];
}

export interface RecentPerformanceItem {
  year: number;
  tournamentName: string;
  generation: string;
  bestResult: string;
  totalTeams: number;
  totalMatches: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  totalGoals: number;
  totalGoalsAgainst: number;
  interPrefectureMatches: number;
  interPrefectureWins: number;
  interPrefectureWinRate: number;
  defeatedPrefectures: string[];
  lostToPrefectures: string[];
}

export interface TopWinRatePrefectureItem {
  rank: number;
  prefectureId: number;
  prefectureName: string;
  totalMatches: number;
  totalWins: number;
  totalLosses: number;
  winRate: number;
}

export interface TopMatchCountPrefectureItem {
  rank: number;
  prefectureId: number;
  prefectureName: string;
  totalMatches: number;
  totalWins: number;
  totalLosses: number;
  winRate: number;
}

export interface PrefectureDashboardResponse {
  summary: PrefectureSummary;
  generationStats: PrefectureGenerationStats[];
  interPrefectureAnalysis: InterPrefectureAnalysis;
  prefectureRanking: PrefectureRankingItem[];
  championshipHistory: ChampionshipHistoryItem[];
  recentPerformance: RecentPerformanceItem[];
}

// API エラーの型定義
export interface ApiError {
  message: string;
  status: number;
}
